import { Component, effect, inject } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { SectionComponent } from '../../../../components/layout/section/section.component';
import { SectionHeaderComponent } from '../../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../../components/layout/section/section-header/section-headline/section-headline.component';
import { CmsTextProviderService } from '../../../../services/cms-text-provider.service';
import { OperatorMappingStore } from '../../../../services/api/operator-mapping/operator-mapping-store.service';
import { DialogService } from '../../../../services/support/dialog/dialog.service';
import { EditOperatorMappingDialog } from '../../../../components/dialogs/edit-operator-mapping-dialog/edit-operator-mapping-dialog.component';
import { LoadingAnimationComponent } from '../../../../components/feedback/loading-animation/loading-animation.component';
import {
  IOperatorMappingForm,
  OperatorMappingFactory,
} from '../../../../factory/operator-mapping/operator-mapping-factory.service';
import { ButtonComponent } from '../../../../components/feedback/button/button.component';
import { IOperatorMapping, ITariffSort, OperatorMappingId } from '@web/dtos';
import { DeleteOperatorMappingDialog } from '../../../../components/dialogs/delete-operator-mapping-dialog/delete-operator-mapping-dialog.component';
import { SortsStore } from '../../../../services/api/sorts/sorts-store.service';

@Component({
  selector: 'aw-on-demand-journey-form',
  imports: [
    ReactiveFormsModule,
    SectionComponent,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    LoadingAnimationComponent,
    ButtonComponent,
  ],
  templateUrl: './configuration-on-demand-journey-form.component.html',
  standalone: true,
})
export class OnDemandJourneyFormComponent {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly formFactory: OperatorMappingFactory = inject(
    OperatorMappingFactory
  );

  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  protected readonly operatorMappingStore: OperatorMappingStore =
    inject(OperatorMappingStore);
  protected operatorMappingForms: FormArray<FormGroup<IOperatorMappingForm>> =
    this.formBuilder.array<FormGroup<IOperatorMappingForm>>([]);
  protected readonly sortsStore: SortsStore = inject(SortsStore);

  constructor() {
    effect(() => {
      this.operatorMappingForms = this.formFactory.buildForm(
        this.operatorMappingStore.operatorMappings()
      );
    });
  }

  protected openCreateOperatorMappingDialog(): void {
    const mappingForm: FormGroup<IOperatorMappingForm> =
      this.formFactory.buildOperatorMappingFormGroup();
    const dialogRef = this.dialogService.openDialog<{
      operatorMapping: FormGroup<IOperatorMappingForm>;
    }>(EditOperatorMappingDialog, {
      headline: this.cmsTextProvider.configuration.createOperatorMapping,
      width: '800px',
      operatorMapping: mappingForm,
    });
    dialogRef.closed.subscribe(response => {
      if (response?.submit) {
        this.operatorMappingForms.controls.push(mappingForm);
        this.operatorMappingStore.saveMapping(
          mappingForm.value as IOperatorMapping
        );
      }
    });
  }

  protected openEditOperatorMappingDialog(
    mapping: FormGroup<IOperatorMappingForm>,
    index: number
  ): void {
    const originalValue: IOperatorMapping =
      mapping.getRawValue() as IOperatorMapping;
    const dialogRef = this.dialogService.openDialog<{
      operatorMapping: FormGroup<IOperatorMappingForm>;
    }>(EditOperatorMappingDialog, {
      headline: this.cmsTextProvider.configuration.editOperatorMapping,
      width: '800px',
      operatorMapping: mapping,
    });
    dialogRef.closed.subscribe(response => {
      if (response?.submit) {
        this.operatorMappingStore.saveMappings(
          this.operatorMappingForms.getRawValue() as IOperatorMapping[]
        );
      } else {
        this.operatorMappingForms.controls[index] =
          this.formFactory.buildOperatorMappingFormGroup(originalValue);
        this.operatorMappingForms.updateValueAndValidity();
      }
    });
  }

  protected openDeleteOperatorMappingDialog(
    mapping: FormGroup<IOperatorMappingForm>,
    index: number
  ): void {
    const dialogRef = this.dialogService.openDialog<{
      operatorMappingId: OperatorMappingId | null;
      operatorName: string | null;
      index: number;
    }>(DeleteOperatorMappingDialog, {
      headline: this.cmsTextProvider.configuration.deleteOperatorMapping,
      width: '800px',
      operatorMappingId: mapping.value.id || null,
      operatorName: mapping.value.name || null,
      index,
    });
    dialogRef.closed.subscribe(response => {
      if (response?.submit) {
        if (response.data.operatorMappingId) {
          this.operatorMappingStore.deleteMapping(
            response.data.operatorMappingId
          );
        } else {
          this.operatorMappingForms.removeAt(response.data.index);
          this.operatorMappingForms.updateValueAndValidity();
        }
      }
    });
  }

  protected getSortName(externalRef: string | null | undefined): string {
    if (!externalRef) {
      return '–';
    }
    const sort: ITariffSort | undefined =
      this.sortsStore.sorts().find(
        s => s.externalReference === externalRef
      );
    return sort ? `${sort.name} (${externalRef})`:
      externalRef;
  }
}
